import type { ReactElement } from 'react'
import { SplitBar } from 'app/components/action-center/SplitBar'
import { microMoney, statusTotals, STATUS_META, STATUS_ORDER } from 'app/components/action-center/treatmentDisplay'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { cn } from 'app/lib/utils'
import type { TreatmentCard, TreatmentStatus } from 'app/types/treatment'
import { formatCurrency } from 'lib/formatCurrency'
import { TREATMENT_LIST } from 'lib/systemLists'

/** The four figures spelled out in words and exact dollars, for the bar's tooltip. */
function TreatmentBreakdown({ card }: { card: TreatmentCard }) {
  const totals = statusTotals(card)
  return (
    <div className="w-48 px-1 py-1 text-left">
      {STATUS_ORDER.map((key) =>
        totals[key] > 0 ? (
          <p key={key} className="flex items-center justify-between gap-3 py-0.5 text-inverse">
            <span className="inline-flex items-center gap-1.5">
              <span className={cn(`h-1.5 w-1.5 rounded-full`, STATUS_META[key].bar)} />
              {STATUS_META[key].label}
            </span>
            <span className="tabular-nums">{formatCurrency(totals[key])}</span>
          </p>
        ) : null,
      )}
    </div>
  )
}

export function SplitBarSection({ card }: { card: TreatmentCard }): ReactElement | null {
  // The headline's figure is left out of the legend so it is printed once (see `CaseHeaderSection`).
  const headline: TreatmentStatus = card.treatmentStatus === TREATMENT_LIST.bookedStage ? `scheduled` : `open`
  const totals = statusTotals(card)
  const legend = STATUS_ORDER.filter((key) => key !== headline && totals[key] > 0)
  if (legend.length === 0) return null
  return (
    <TooltipWrapper placement="top" message={<TreatmentBreakdown card={card} />}>
      <div className="mt-2.5 flex cursor-help items-center gap-2">
        <div className="min-w-0 flex-1">
          <SplitBar card={card} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {legend.map((key) => (
            <span key={key} className="inline-flex items-center gap-1 text-body-xs text-subtle">
              <span className={cn(`h-1.5 w-1.5 shrink-0 rounded-full`, STATUS_META[key].bar)} />
              {STATUS_META[key].label} {microMoney(totals[key])}
            </span>
          ))}
        </div>
      </div>
    </TooltipWrapper>
  )
}
